import * as React from 'react';
import { styled } from '@linaria/react';

import { normalize } from '@/utils';
import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  Svg,
  CodeSnippet,
  PositionDot,
  Label,
} from './BasicShapes.shared';
import GuideLine from './GuideLine';
import { getEllipseCircumference } from './BasicShapes.helpers';

const SIZE = 340;

function RoundedRectangleDemo() {
  const playTick = useTickSfx();

  return (
    <Demo
      id="centered-stroke-demo"
      outputWrapper={OutputWrapper}
      initialValues={{
        rx: 100,
        ry: 50,
      }}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          highlightControlWhenActive
          key="rx"
          id="rx"
          label="rx"
          secondaryLabel="(horizontal radius)"
          min={20}
          max={200}
          step={10}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="ry"
          id="ry"
          label="ry"
          secondaryLabel="(vertical radius)"
          min={20}
          max={200}
          step={10}
          onChange={() => {
            playTick();
          }}
        />,
      ]}
    >
      {({ rx, ry }, activeControlId) => {
        const circumference = getEllipseCircumference(rx, ry);
        const code = `\
<svg width="${SIZE}" height="${SIZE}">
  <rect
    x="80"
    y="100"
    width="500"
    height="500"
    rx="${rx}"
    ry="${ry}"
    stroke="green"
    stroke-width="5"
    fill="none"
  />
</svg>\
`;
        return (
          <Wrapper>
            <StyledCodeSnippet code={code} />

            <SvgWrapper>
              <Svg size={SIZE} fill="none">
                <ellipse
                  cx={80 + rx}
                  cy={100 + ry}
                  rx={rx}
                  ry={ry}
                  fill="none"
                  stroke="white"
                  strokeWidth={Math.round(
                    normalize(rx + ry, 40, 400, 3, 8)
                  )}
                  strokeOpacity={0.5}
                  strokeDasharray={`${circumference / 50} ${
                    circumference / 40
                  }`}
                  transform={`rotate(180 ${80 + rx} ${100 + ry})`}
                  strokeLinecap="round"
                />
                <GuideLine
                  x1={80 + rx - 6}
                  y1={100 + ry}
                  x2={80 + 0}
                  y2={100 + ry}
                  stroke="var(--wham-primary-500)"
                  isActive={activeControlId === 'rx'}
                />
                <GuideLine
                  x1={80 + rx}
                  y1={100 + ry - 6}
                  x2={80 + rx}
                  y2={100 + 0}
                  stroke="var(--wham-primary-500)"
                  isActive={activeControlId === 'ry'}
                />
                <rect
                  x={80}
                  y={100}
                  width={500}
                  height={500}
                  strokeWidth={5}
                  rx={rx}
                  ry={ry}
                  stroke="var(--wham-complementary-500)"
                />
                <Label
                  x={80 + rx / 2}
                  y={100 + ry + 16}
                  textAnchor="middle"
                  dominantBaseline="bottom"
                  data-is-hidden={rx < 30}
                  data-is-active={activeControlId === 'rx'}
                  style={{
                    fontSize: '0.8rem',
                    opacity: rx >= 40 ? 1 : 0,
                  }}
                >
                  rx
                </Label>
                <Label
                  x={80 + rx + 16}
                  y={100 + ry / 2}
                  textAnchor="start"
                  dominantBaseline="middle"
                  data-is-hidden={ry < 30}
                  data-is-active={activeControlId === 'ry'}
                  style={{
                    fontSize: '0.8rem',
                    opacity: ry >= 30 ? 1 : 0,
                  }}
                >
                  ry
                </Label>

                <PositionDot cx={80 + rx} cy={100 + ry} />
              </Svg>
            </SvgWrapper>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

const HIDE_SNIPPET_AT = '38rem';

const OutputWrapper = styled(Space)`
  container-type: inline-size;
`;

const ControlsGrid = styled.div`
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 32rem) {
    grid-template-columns: 1fr;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;
  gap: 16px;
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  max-height: ${SIZE}px;
  overflow-x: auto;
  overflow-y: hidden;

  @container (max-width: ${HIDE_SNIPPET_AT}) {
    display: none;
  }
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: ${HIDE_SNIPPET_AT}) {
    position: relative;
  }
`;

export default RoundedRectangleDemo;
